<template>
  <div class="bottom-box" :class="wrapClass">
    <div class="left-wrap">
      <woot-button
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        :is-disabled="!canReply && !isOnPrivateNote"
        icon="emoji"
        emoji="😊"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        icon-size="16"
        @click="toggleEmojiPicker"
      />
      <span
        :class="{ wrapper__disabled: !canReply && !isOnPrivateNote }"
        style="margin-top: 5px;"
      >
        <file-upload
          ref="upload"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          :size="4096 * 4096"
          :accept="allowedFileTypes"
          :multiple="enableMultipleFileUpload"
          :drop="true"
          :drop-directory="false"
          :class="{ 'file-upload__disabled': !canReply && !isOnPrivateNote }"
          :data="{
            direct_upload_url: '/rails/active_storage/direct_uploads',
            direct_upload: true,
          }"
          @input-file="onFileUpload"
        >
          <woot-button
            v-if="showAttachButton"
            class-names="button--upload"
            :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            :is-disabled="!canReply && !isOnPrivateNote"
            icon="attach"
            emoji="📎"
            color-scheme="secondary"
            variant="smooth"
            size="small"
            icon-size="16"
          />
        </file-upload>
      </span>
      <woot-button
        v-if="showAudioRecorderButton"
        :icon="!isRecordingAudio ? 'microphone' : 'microphone-off'"
        emoji="🎤"
        :color-scheme="!isRecordingAudio ? 'secondary' : 'alert'"
        variant="smooth"
        size="small"
        icon-size="16"
        :title="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :is-disabled="disabledAudioRecorderButton"
        @click="toggleAudioRecorder"
      />
      <woot-button
        v-if="showEditorToggle"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        :is-disabled="!canReply && !isOnPrivateNote"
        icon="quote"
        emoji="🖊️"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        icon-size="16"
        @click="$emit('toggle-editor')"
      />
      <woot-button
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        emoji="🎤"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        icon-size="16"
        @click="toggleAudioRecorderPlayPause"
      >
        <span>{{ recordingAudioDurationText }}</span>
      </woot-button>
      <woot-button
        v-if="showMessageSignatureButton"
        v-tooltip.top-end="signatureToggleTooltip"
        icon="signature"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        icon-size="16"
        :is-disabled="!canReply && !isOnPrivateNote"
        :title="signatureToggleTooltip"
        @click="toggleMessageSignature"
      />
      <woot-button
        v-if="showWhatsappButtonTemplates && !isNote"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.WHATSAPP_TEMPLATE')"
        icon="whatsapp"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        icon-size="16"
        :is-disabled="whatsappTemplatesDisabled"
        :title="$t('CONVERSATION.REPLYBOX.WHATSAPP_TEMPLATE')"
        @click="$emit('selectWhatsappTemplate')"
      />
      <woot-button
        v-if="showMessengerButtonTemplates && !isNote"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.MESSENGER_TEMPLATE')"
        icon="brand-facebook"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        icon-size="16"
        :is-disabled="messengerTemplatesDisabled"
        :title="$t('CONVERSATION.REPLYBOX.MESSENGER_TEMPLATE')"
        @click="$emit('selectMessengerTemplate')"
      />
      <video-call-button
        v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
        :conversation-id="conversationId"
      />
      <transition name="modal-fade">
        <div
          v-show="$refs.upload && $refs.upload.dropActive"
          class="modal-mask"
        >
          <fluent-icon icon="cloud-backup" />
          <h4 class="page-sub-title">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
    </div>
    <div class="right-wrap">
      <woot-button
        size="small"
        icon-size="16"
        :class-names="buttonClass"
        :is-disabled="isSendDisabled"
        @click="onSend"
      >
        {{ sendButtonText }}
      </woot-button>
    </div>
  </div>
</template>

<script>
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import { hasPressedAltAndAKey } from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP,
} from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton';
import { REPLY_EDITOR_MODES } from './constants';
import { mapGetters } from 'vuex';

export default {
  name: 'ReplyBottomPanel',
  components: { FileUpload, VideoCallButton },
  mixins: [eventListenerMixins, uiSettingsMixin, inboxMixin],
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '',
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    showEmojiPicker: {
      type: Boolean,
      default: false,
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    showEditorToggle: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    showWhatsappButtonTemplates: {
      type: Boolean,
      default: false,
    },
    // Deshabilita el botón de plantillas de WhatsApp (ej. canal pendiente de reautorización).
    whatsappTemplatesDisabled: {
      type: Boolean,
      default: false,
    },
    showMessengerButtonTemplates: {
      type: Boolean,
      default: false,
    },
    // Deshabilita el botón de plantillas de Messenger (ej. canal pendiente de reautorización).
    messengerTemplatesDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    canReply: {
      type: Boolean,
      default: true,
    },
    // Objeto para deshabilitar boton para grabar audio
    isAudioRecorderDisabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    isNote() {
      return this.mode === REPLY_EDITOR_MODES.NOTE;
    },
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    buttonClass() {
      return {
        warning: this.isNote,
        primary: !this.isNote,
      };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      // Disable audio recorder for safari browser as recording is not supported
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
        navigator.userAgent
      );
      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) &&
        this.showAudioRecorder &&
        !isSafari
      );
    },
    // Deshabilita el botón para grabar audio cuando es respuesta de mensaje y Evolution API
    disabledAudioRecorderButton() {
      return (
        (!this.canReply && !this.isOnPrivateNote) ||
        this.isAudioRecorderDisabled
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    allowedFileTypes() {
      if (this.isATwilioWhatsAppChannel) {
        return ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP;
      }
      return ALLOWED_FILE_TYPES;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'microphone-pause';
        case 'paused':
          return 'microphone-play';
        case 'stopped':
          return 'microphone-play';
        default:
          return 'microphone-stop';
      }
    },
    showMessageSignatureButton() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    sendWithSignature() {
      const { send_with_signature: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    handleKeyEvents(e) {
      if (hasPressedAltAndAKey(e)) {
        this.$refs.upload.$children[1].$el.click();
      }
    },
    toggleMessageSignature() {
      this.updateUISettings({
        send_with_signature: !this.sendWithSignature,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.bottom-box {
  display: flex;
  justify-content: space-between;
  padding: var(--space-slab) var(--space-normal);

  &.is-note-mode {
    background: var(--conversa2-orange-50-color);
  }
}

.left-wrap .button {
  margin-right: var(--space-small);
  background-color: var(--conversa2-blue-200-color);

  &:hover {
    background-color: #d4eef9;
  }
}

.left-wrap {
  align-items: center;
  display: flex;
}

.file-upload__disabled {
  pointer-events: none;
}

.wrapper__disabled {
  cursor: not-allowed;
}

.right-wrap {
  display: flex;
  .button {
    &.primary {
      background-color: var(--conversa2-blue-700-color);
      font-size: var(--font-size-small);
      font-weight: var(--font-weight-bold);
    }
    &.warning {
      background-color: var(--conversa2-yellow-700-color);
      font-size: var(--font-size-small);
      font-weight: var(--font-weight-bold);
      &:hover {
        background-color: var(--conversa2-yellow-900-color);
      }

      &[disabled].warning,
      &.disabled.warning {
        opacity: 1;
        cursor: not-allowed;
        background-color: #f8d473;
      }
    }
  }
}

::v-deep .file-uploads {
  label {
    cursor: pointer;
  }
  &:hover .button {
    background: #d4eef9;
  }
}

.modal-mask {
  color: var(--s-600);
  background: var(--white-transparent);
  flex-direction: column;
}

.page-sub-title {
  color: var(--s-600);
}

.icon {
  font-size: 8rem;
}

.button {
  &.smooth {
    &.secondary {
      color: var(--conversa2-neutral-800-color);
    }
  }
}
</style>
